import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formService, Form, FormField } from '@/firebase/formService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';
import { useAuth } from '@/contexts/AuthContext';
import { getUserPreferredLanguage, getFormName, getFormDescription } from '@/utils/formLanguageHelper';
import { usePreview } from '@/contexts/PreviewContext';
import { 
  fetchUserRoles,
  normalizeRoles,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isSystemUserInOrganization,
  isManagerInOrganization
} from '@/utils/roleHelpers';
import { SYSTEM_ROLES } from '@/constants/roles';
import { Plus, Trash2, Pencil, Sparkles, Play, AlertCircle, FileText, Image, ExternalLink, History, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FormForm from './FormForm';
import RunForm from './RunForm';
import RunTableForm from './RunTableForm';
import FormHistory from './FormHistory';
import FormQuickSetupWizard from './FormQuickSetupWizard';
import { aiSearchService, NewFormSuggestion } from '@/services/aiSearchService';
import ContextualHelp from './ContextualHelp';
import { formCompletionService } from '@/firebase/formCompletionService';

const FormManagement: React.FC = () => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const { hasFeature, subscription } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const preferredLanguage = getUserPreferredLanguage(user);
  const [forms, setForms] = useState<Form[]>([]);
  const [templates, setTemplates] = useState<Record<string, ComplianceTemplate>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [formForHistory, setFormForHistory] = useState<Form | null>(null);
  const [newFormSuggestions, setNewFormSuggestions] = useState<NewFormSuggestion[] | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [formToAdd, setFormToAdd] = useState<Partial<Form> | null>(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isQuickSetupOpen, setIsQuickSetupOpen] = useState(false);
  const [quickSetupInitialData, setQuickSetupInitialData] = useState<{
    name?: string;
    description?: string;
    fields?: FormField[];
    templateId?: string;
  } | undefined>();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [isSystemManager, setIsSystemManager] = useState<boolean>(false);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [locations, setLocations] = useState<Record<string, Location>>({});
  const [lastCompletionDates, setLastCompletionDates] = useState<Record<string, Date | null>>({});

  useEffect(() => {
    loadUserRoles();
    loadUserLocations();
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId, previewState?.role]);

  useEffect(() => {
    // Wait for user locations to be loaded before loading data for system users
    if (isSystemUser && user?.id && currentOrganization?.id && userLocationIds.length === 0) {
      // Still loading user locations, wait
      return;
    }
    loadData();
  }, [currentOrganization?.id, user?.id, userLocationIds.length, isSystemUser]);

  const loadUserRoles = async () => {
    // In preview mode, use preview user's roles
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const previewUserRef = doc(db, 'users', previewState.previewUserId);
        const previewUserSnap = await getDoc(previewUserRef);
        
        if (previewUserSnap.exists()) {
          const previewUserData = previewUserSnap.data();
          const organizationId = currentOrganization?.id;
          
          // Use organization-specific roles if organization is available
          if (organizationId) {
            const ownerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
            setUserOwnerRoles(ownerRoles);
            setIsSystemUser(isSystemUserInOrganization(previewUserData, organizationId));
            setIsSystemManager(isManagerInOrganization(previewUserData, organizationId));
          } else {
            // Fallback to global roles if no organization
            const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
            const ownerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES.includes(r.toLowerCase() as any));
            setUserOwnerRoles(ownerRoles);
            const systemRoles = previewUserRoles.filter((r: string) => SYSTEM_ROLES.includes(r.toLowerCase() as any));
            const isSystemUserRole = systemRoles.length === 1 && systemRoles[0].toLowerCase() === 'user';
            setIsSystemUser(isSystemUserRole);
            const isManagerRole = previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            setIsSystemManager(isManagerRole);
          }
        }
      } catch (error) {
        console.error('Error loading preview user roles:', error);
      }
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: determine permissions based on role
      const role = previewState.role.toLowerCase();
      const isSystemUserRole = role === 'user';
      const isManagerRole = role === 'manager' || role === 'admin';
      
      setIsSystemUser(isSystemUserRole);
      setIsSystemManager(isManagerRole);
      setUserOwnerRoles([]); // No owner roles when previewing by role
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's roles for current organization
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific roles
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
          const isManagerRole = isManagerInOrganization(userData, organizationId);
          
          setUserOwnerRoles(ownerRoles);
          setIsSystemUser(isSystemUserRole);
          setIsSystemManager(isManagerRole);
        } else {
          // Fallback to legacy fetchUserRoles
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsSystemUser(roles.isSystemUser);
          setIsSystemManager(roles.isSystemManager);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to legacy fetchUserRoles
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsSystemUser(roles.isSystemUser);
        setIsSystemManager(roles.isSystemManager);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - use legacy roles
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsSystemUser(roles.isSystemUser);
      setIsSystemManager(roles.isSystemManager);
    }
  };

  const loadUserLocations = async () => {
    // In preview mode, use preview user's locationIds
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.locationIds) {
      setUserLocationIds(previewState.locationIds);
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: no location restrictions (show all)
      setUserLocationIds([]);
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's locationIds
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const locationIds = userData.locationIds || [];
          setUserLocationIds(locationIds);
        }
      } catch (error) {
        console.error('Error loading user locations:', error);
        setUserLocationIds([]);
      }
    } else {
      setUserLocationIds([]);
    }
  };

  const loadData = async () => {
    await Promise.all([loadForms(), loadTemplates(), loadLocations()]);
  };

  const loadLocations = async () => {
    if (!currentOrganization?.id) {
      setLocations({});
      return;
    }
    try {
      const result = await locationService.getAllLocations(currentOrganization.id);
      if (result.success && result.data) {
        const locationMap: Record<string, Location> = {};
        result.data.forEach(location => {
          if (location.id) {
            locationMap[location.id] = location;
          }
        });
        setLocations(locationMap);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadForms = async () => {
    if (!currentOrganization?.id) {
      setForms([]);
      return;
    }

    setIsLoading(true);
    try {
      const result = await formService.getAllForms(currentOrganization.id);
      if (result.success && result.data) {
        setForms(result.data);
        
        // Load last completion dates for all forms
        const completionDatesMap: Record<string, Date | null> = {};
        await Promise.all(
          result.data.map(async (form) => {
            if (!form.id) return;
            try {
              const completionResult = await formCompletionService.getCompletionsForForm(form.id);
              if (completionResult.success && completionResult.data && completionResult.data.length > 0) {
                // Get the most recent completion
                const sortedCompletions = [...completionResult.data].sort((a, b) => {
                  const dateA = a.completionDate?.toDate ? a.completionDate.toDate().getTime() : 
                               (a.completionDate instanceof Date ? a.completionDate.getTime() : 
                               (typeof a.completionDate === 'number' ? a.completionDate : 0));
                  const dateB = b.completionDate?.toDate ? b.completionDate.toDate().getTime() : 
                               (b.completionDate instanceof Date ? b.completionDate.getTime() : 
                               (typeof b.completionDate === 'number' ? b.completionDate : 0));
                  return dateB - dateA; // Descending order
                });
                
                const lastCompletion = sortedCompletions[0];
                if (lastCompletion?.completionDate) {
                  const date = lastCompletion.completionDate?.toDate ? 
                              lastCompletion.completionDate.toDate() : 
                              (lastCompletion.completionDate instanceof Date ? 
                              lastCompletion.completionDate : 
                              new Date(lastCompletion.completionDate));
                  completionDatesMap[form.id] = date;
                } else {
                  completionDatesMap[form.id] = null;
                }
              } else {
                completionDatesMap[form.id] = null;
              }
            } catch (error) {
              console.error(`Error loading completion date for form ${form.id}:`, error);
              completionDatesMap[form.id] = null;
            }
          })
        );
        setLastCompletionDates(completionDatesMap);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      showNotification('Failed to load forms', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const result = await complianceService.getAllTemplates(currentOrganization?.id);
      if (result.success && result.data) {
        const templatesMap: Record<string, ComplianceTemplate> = {};
        result.data.forEach(template => {
          if (template.id) {
            templatesMap[template.id] = template;
          }
        });
        setTemplates(templatesMap);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Debounced AI search when no results are found
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm) {
      setNewFormSuggestions(null);
      return;
    }

    const currentFiltered = forms.filter(form =>
      form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && searchTerm.length >= 3) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAI(true);
        setNewFormSuggestions(null);
        
        try {
          const existingFormNames = forms.map(f => f.name);
          const availableTemplates = Object.values(templates);
          
          if (availableTemplates.length === 0) {
            setIsLoadingAI(false);
            setNewFormSuggestions([] as any);
            return;
          }
          
          // Try AI generation for forms
          const newSuggestions = await aiSearchService.generateNewFormSuggestions(
            searchTerm, 
            existingFormNames,
            availableTemplates
          );
          
          if (newSuggestions && newSuggestions.length > 0) {
            setNewFormSuggestions(newSuggestions);
          } else {
            // No suggestions from AI - show message that AI search is working but found nothing
            setNewFormSuggestions([] as any);
          }
        } catch (error) {
          console.error('Error fetching AI form suggestions:', error);
        } finally {
          setIsLoadingAI(false);
        }
      }, 800);
    } else {
      setNewFormSuggestions(null);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, forms, templates]);

  const handleAdd = () => {
    // Check if user has permission to add forms (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add forms', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }
    setIsAdding(true);
    setIsEditing(false);
    setSelectedForm(null);
    setFormToAdd(null);
  };

  const handleEdit = (form: Form) => {
    // Check if user has permission to edit forms (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can edit forms', 'error');
      return;
    }
    setSelectedForm(form);
    setIsEditing(true);
    setIsAdding(false);
    setFormToAdd(null);
  };

  const handleDelete = async (id: string) => {
    // Check if user has permission to delete forms (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete forms', 'error');
      return;
    }
    if (!confirm('Are you sure you want to delete this form?')) return;
    
    const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
    const result = await formService.deleteForm(id, userInfo, currentOrganization?.id);
    if (result.success) {
      await loadForms();
      showNotification('Form deleted successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };


  const handleRunForm = (form: Form) => {
    // Check if user is on free plan
    const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
    const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
    const isSubscriptionCancelled = subscription?.status === 'canceled';
    
    if (isFreePlan && !isSubscriptionCancelled) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSelectedForm(form);
    setIsRunning(true);
  };

  const handleCloseRunForm = () => {
    setIsRunning(false);
    setSelectedForm(null);
  };

  const handleViewHistory = (form: Form) => {
    setFormForHistory(form);
    setIsViewingHistory(true);
  };

  const handleFormSubmit = async (data: Partial<Form>) => {
    // Check if user has permission (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add or edit forms', 'error');
      setIsAdding(false);
      setIsEditing(false);
      return;
    }

    if (isAdding) {
      if (!currentOrganization?.id) {
        setShowOrgModal(true);
        setIsAdding(false);
        return;
      }
      if (!data.name || !data.templateId) {
        showNotification('Name and Compliance are required', 'error');
        return;
      }
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await formService.addForm(data as Omit<Form, 'id'>, currentOrganization?.id, userInfo);
      if (result.success) {
        await loadForms();
        showNotification('Form added successfully', 'success');
        setIsAdding(false);
        setFormToAdd(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } else if (isEditing && selectedForm?.id) {
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await formService.updateForm(selectedForm.id, data, userInfo, currentOrganization?.id);
      if (result.success) {
        await loadForms();
        showNotification('Form updated successfully', 'success');
        setIsEditing(false);
        setFormToAdd(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    }
  };

  const handleFormCancel = () => {
    setIsAdding(false);
    setIsEditing(false);
    setSelectedForm(null);
    setFormToAdd(null);
  };

  const handleAddFromSuggestion = (suggestion: NewFormSuggestion) => {
    // Check if user has permission to add forms (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add forms', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }

    // Convert AI suggestion to form format
    // Convert string[] variables to FormVariable[] format
    const variables = suggestion.variables.length > 0 
      ? suggestion.variables.map(name => ({
          name,
          possibleValues: []
        }))
      : undefined;
    
    // Convert suggestion fields to FormField[] format
    const fields: Form['fields'] = suggestion.fields.map((field, idx) => ({
      label: field.label,
      type: (field.type || 'text') as FormField['type'],
      order: idx + 1,
      required: field.required || false
    }));
    
    // Open Quick Setup wizard with pre-filled data from AI suggestion
    setQuickSetupInitialData({
      name: suggestion.name,
      description: suggestion.description,
      fields: fields,
      templateId: suggestion.templateId
    });
    setIsQuickSetupOpen(true);
    // Clear search to see the new form after creation
    setSearchTerm('');
  };

  // Compute accessible template IDs once based on user's locations
  // This mapping is created only once and reused for all form filtering
  const accessibleTemplateIds = useMemo(() => {
    // If not a system user or no locations, return empty set
    if (!isSystemUser || userLocationIds.length === 0 || Object.keys(locations).length === 0) {
      return new Set<string>();
    }

    const templateIds = new Set<string>();
    
    userLocationIds.forEach(locationId => {
      const location = locations[locationId];
      if (location) {
        const locationTemplates = location.assignedTemplates;
        if (Array.isArray(locationTemplates)) {
          locationTemplates.forEach(templateId => {
            if (templateId) {
              templateIds.add(templateId);
            }
          });
        }
      }
    });
    
    return templateIds;
  }, [isSystemUser, userLocationIds, locations]);

  // Filter forms
  const filteredForms = useMemo(() => {
    return forms.filter(form => {
      // Apply search filter
      const matchesSearch = form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) {
        return false;
      }
      
      // For users with system role "User", filter by owner roles AND location access
      if (isSystemUser) {
        // If form has no compliance template, don't show it to regular users
        if (!form.templateId) {
          return false;
        }
        
        // Check if the compliance template's owner_role matches user's owner roles
        const template = templates[form.templateId];
        if (!template) {
          return false;
        }
        
        const templateOwnerRole = template.owner_role;
        if (!templateOwnerRole || !userOwnerRoles.includes(templateOwnerRole)) {
          return false;
        }
        
        // Filter by location access
        // Priority 1: If form has locationIds, check direct location match
        // Priority 2: Fall back to template-based filtering (backward compatible)
        if (userLocationIds.length > 0) {
          // Wait for locations to be loaded before filtering
          if (Object.keys(locations).length === 0) {
            return false;
          }
          
          // If form has locationIds specified, must match user's accessible locations
          if (form.locationIds && form.locationIds.length > 0) {
            const hasLocationAccess = form.locationIds.some(locationId => 
              userLocationIds.includes(locationId)
            );
            if (!hasLocationAccess) {
              return false; // Form is restricted to locations user doesn't have access to
            }
          } else {
            // Backward compatibility: Use template-based filtering if no locationIds
            // Form is visible only if its templateId is in the accessible templates
            if (!form.templateId || !accessibleTemplateIds.has(form.templateId)) {
              return false;
            }
          }
        }
        
        return true;
      }
      
      // Admin and Manager system roles see all forms
      return true;
    });
  }, [forms, searchTerm, isSystemUser, templates, userOwnerRoles, userLocationIds, locations, accessibleTemplateIds]);

  return (
    <div className="p-4 max-w-[95vw] mx-auto relative">
      {/* Backdrop overlay when panel is open */}
      {(isAdding || isEditing) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
          onClick={handleFormCancel}
        />
      )}
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-800">Form Management</h2>
            <ContextualHelp category="Forms" tooltip="Help with Forms" />
          </div>
          <div className="flex gap-2">
            {isSystemManager && (
              <button
                onClick={() => {
                  if (!currentOrganization?.id) {
                    setShowOrgModal(true);
                    return;
                  }
                  setIsQuickSetupOpen(true);
                }}
                className="btn-primary px-4 py-2 flex items-center gap-2"
                title="Create form, template, and link in one flow"
              >
                <Plus className="w-4 h-4" /> Create Form
              </button>
            )}
            <button onClick={loadForms} className="btn-secondary px-4 py-2">
              Refresh
            </button>
          </div>
        </div>

        {/* Educational Banner for Free Plan */}
        {(() => {
          const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
          const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
          const isSubscriptionCancelled = subscription?.status === 'canceled';
          
          if (isFreePlan && !isSubscriptionCancelled) {
            return (
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Info className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Forms Available</h3>
                    <p className="text-sm text-blue-800 mb-4">
                      You can create and manage forms on your current plan. However, <strong>running forms requires an Operate or Comply plan</strong>. Upgrade to enable running forms and collecting data.
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Create reusable forms (e.g., Inspection Forms, Audit Checklists) and tie them to compliance items. Then run these forms per location and fill them with data.
          </p>
        </div>

        {/* Smart Suggestion: No Templates Available */}
        {Object.keys(templates).length === 0 && isSystemManager && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-900 mb-1">
                  No Compliance Templates Available
                </p>
                <p className="text-sm text-orange-800 mb-3">
                  You need at least one compliance template to create forms. Templates define what needs to be compliant. You can create one inline when adding a form, or create templates first in Compliance Management.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate('/compliance-management')}
                    className="px-3 py-1.5 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                  >
                    Go to Compliance Management
                  </button>
                  <button
                    onClick={handleAdd}
                    className="px-3 py-1.5 bg-white text-orange-700 border border-orange-300 rounded-md hover:bg-orange-50 transition-colors text-sm font-medium"
                  >
                    Create Template Inline
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search forms... (AI-powered suggestions available)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
            />
            {isLoadingAI && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
              </div>
            )}
          </div>
        </div>

        {/* Forms Table */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading forms...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Locations</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Variables</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Last Completed</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredForms.map((form, index) => (
                  <tr key={form.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="font-medium text-sm text-gray-900">{getFormName(form, preferredLanguage)}</div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="text-xs text-gray-700">
                        {getFormDescription(form, preferredLanguage) || 'No description'}
                        {form.referenceDocuments && form.referenceDocuments.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                            {form.referenceDocuments.map((doc, docIdx) => (
                              <a
                                key={docIdx}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                title={doc.name}
                              >
                                {doc.type === 'pdf' ? (
                                  <FileText className="w-3 h-3" />
                                ) : (
                                  <Image className="w-3 h-3" />
                                )}
                                <span className="text-xs">{doc.name}</span>
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {form.templateId && templates[form.templateId] ? (
                        <span className="text-xs text-gray-900">{templates[form.templateId].name}</span>
                      ) : form.templateId ? (
                        <span className="text-xs text-gray-700 italic">{form.templateId}</span>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {form.locationIds && form.locationIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {form.locationIds.slice(0, 3).map((locationId) => {
                            const location = locations[locationId];
                            return (
                              <span
                                key={locationId}
                                className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 text-xs"
                              >
                                {location?.name || locationId}
                              </span>
                            );
                          })}
                          {form.locationIds.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs">
                              +{form.locationIds.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 italic">All locations</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {form.variables && form.variables.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {form.variables.map((variable, idx) => {
                            const varName = typeof variable === 'string' ? variable : variable.name;
                            const varValues = typeof variable === 'string' ? [] : (variable.possibleValues || []);
                            return (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title={varValues.length > 0 ? `${varName} (${varValues.length} values)` : varName}>
                                {varName}
                                {varValues.length > 0 && (
                                  <span className="ml-1 text-blue-600 font-normal">
                                    ({varValues.length})
                                  </span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">None</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {form.id && lastCompletionDates[form.id] ? (
                        <div className="text-xs text-gray-700 bg-yellow-100 px-2 py-1 rounded font-medium">
                          {lastCompletionDates[form.id]!.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleRunForm(form)}
                          className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1"
                          title="Run Form"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewHistory(form)}
                          className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        {isSystemManager && (
                          <>
                            <button
                              onClick={() => handleEdit(form)}
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(form.id!)}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredForms.length === 0 && !isLoading && (
          <div className="text-center py-8">
            {isLoadingAI ? (
              <div className="border border-gray-200 rounded p-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                  <p className="text-lg font-medium text-gray-700">AI is finding suggestions...</p>
                </div>
                <p className="text-sm text-gray-500">Analyzing available templates for you</p>
              </div>
            ) : newFormSuggestions && newFormSuggestions.length === 0 && searchTerm.length >= 3 ? (
              <div className="border border-orange-200 rounded-lg p-6 bg-orange-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-orange-900">AI Search Complete</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-2">
                  {Object.keys(templates).length === 0 
                    ? '⚠️ No compliance templates available. Please create or load default templates first before creating forms.'
                    : 'No AI-generated form suggestions found. Try a different search term or create a form manually.'}
                </p>
                
                {Object.keys(templates).length === 0 && (
                  <button
                    onClick={() => window.location.href = '/compliance-management'}
                    className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                  >
                    Go to Templates →
                  </button>
                )}
              </div>
            ) : newFormSuggestions && newFormSuggestions.length > 0 ? (
              <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">New Form Suggestions</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Based on compliance requirements, here are forms you might want to add to your system:
                </p>

                {/* Show upgrade message for Starter plan */}
                {!hasFeature('ai_assistance') && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800 font-medium mb-2">
                      Upgrade to Standard or Pro plan to add AI-generated forms
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      Upgrade Now
                    </button>
                  </div>
                )}
                
                <div className="space-y-4">
                  {newFormSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                          {suggestion.description && (
                            <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                          )}
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {suggestion.templateName}
                            </span>
                            {suggestion.variables.length > 0 && (
                              <span className="text-xs text-gray-600">
                                Variables: {suggestion.variables.join(', ')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                          
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-700">
                              <span className="font-medium">Form Fields ({suggestion.fields.length}):</span>
                              <ul className="mt-1 space-y-1 ml-4 list-disc">
                                {suggestion.fields.slice(0, 5).map((field, idx) => (
                                  <li key={idx} className="text-gray-600">
                                    {field.label} <span className="text-gray-500">({field.type})</span>
                                    {field.required && <span className="text-red-600 ml-1">*</span>}
                                  </li>
                                ))}
                                {suggestion.fields.length > 5 && (
                                  <li className="text-gray-500 italic">... and {suggestion.fields.length - 5} more fields</li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          if (hasFeature('ai_assistance')) {
                            handleAddFromSuggestion(suggestion);
                          } else {
                            navigate('/subscription');
                          }
                        }}
                        disabled={!hasFeature('ai_assistance')}
                        className={`w-full mt-4 px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 font-medium ${
                          hasFeature('ai_assistance')
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        {hasFeature('ai_assistance') ? 'Add This Form' : 'Upgrade to Add'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                <p className="text-lg font-medium">No forms found</p>
                <p className="text-sm mt-1">
                  {searchTerm 
                    ? 'Try a different search term or clear filters.' 
                    : 'No forms match your filters.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(isAdding || isEditing) && (
        <FormForm
          form={(formToAdd && formToAdd.name && formToAdd.templateId) ? formToAdd as Form : (selectedForm || undefined)}
          forms={forms.filter(f => f.id !== selectedForm?.id)} // Exclude current form if editing
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      {/* Run Form Modal - Route to appropriate component based on layout type */}
      {isRunning && selectedForm && (() => {
        const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
        const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
        const isSubscriptionCancelled = subscription?.status === 'canceled';
        
        // Don't show run modal for free plan users
        if (isFreePlan && !isSubscriptionCancelled) {
          return null;
        }
        
        return selectedForm.layoutType === 'table' ? (
          <RunTableForm
            form={selectedForm}
            onClose={handleCloseRunForm}
            onComplete={async () => {
              await loadForms();
              handleCloseRunForm();
              showNotification('Table form submitted successfully!', 'success');
            }}
          />
        ) : (
          <RunForm
            form={selectedForm}
            onClose={handleCloseRunForm}
            onComplete={async () => {
              await loadForms();
              handleCloseRunForm();
              showNotification('Form submitted successfully!', 'success');
            }}
          />
        );
      })()}

      {/* Form History Modal */}
      {isViewingHistory && formForHistory && (
        <FormHistory
          form={formForHistory}
          onClose={() => {
            setIsViewingHistory(false);
            setFormForHistory(null);
          }}
        />
      )}

      {/* Organization Required Modal */}
      {showOrgModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              You must have an organization to add forms. Please create an organization first.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowOrgModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Setup Wizard */}
      {isQuickSetupOpen && (
        <FormQuickSetupWizard
          initialData={quickSetupInitialData}
          onComplete={async (formId) => {
            setIsQuickSetupOpen(false);
            setQuickSetupInitialData(undefined);
            // Reload both forms and templates to ensure newly created templates are available
            await Promise.all([loadForms(), loadTemplates()]);
            showNotification('Form setup completed successfully!', 'success');
          }}
          onCancel={() => {
            setIsQuickSetupOpen(false);
            setQuickSetupInitialData(undefined);
          }}
        />
      )}

      {/* Floating Getting Started button on empty state */}
      {forms.length === 0 && !isLoading && !showOrgModal && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Upgrade Required</h3>
                <p className="text-sm text-gray-600">Run Form</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              Running forms requires an <strong>Operate</strong> or <strong>Comply</strong> plan. You can create and manage forms on your current plan, but to run them and collect data, upgrade now!
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/subscription');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormManagement;

